import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Receipt, TrendingDown, Fuel, Paintbrush, Wrench, Home, Shield, Phone, Megaphone, Users, Calculator } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { PeriodSelector, KPICard, DepartmentBadge } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { CategoryDetailModal } from "@/components/CategoryDetailModal";
import { formatCurrency, formatPercent, MONTHS, CONFIG, CHART_COLORS } from "@/lib/config";
import { EXPENSE_CATEGORIES } from "@/lib/targets";
import api from "@/lib/api";

const CATEGORY_ICONS = {
  salaires_terrain: Users,
  salaires_admin: Users,
  materiaux: Wrench,
  peinture: Paintbrush,
  essence: Fuel,
  sous_traitants: Users,
  loyer: Home,
  assurances: Shield,
  telecom: Phone,
  saas: Calculator,
  publicite_google: Megaphone,
  publicite_meta: Megaphone,
  interets: Calculator,
  depreciation: TrendingDown,
  dividendes: Receipt,
  autres: Receipt
};

const CATEGORY_COLORS = [
  "#064E3B", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EF4444", 
  "#10B981", "#6366F1", "#EC4899", "#14B8A6", "#F97316",
  "#84CC16", "#06B6D4", "#A855F7", "#22C55E", "#3B82F6"
];

export const Depenses = () => {
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(1);
  const [viewType, setViewType] = useState("mensuel"); // mensuel, trimestriel, annuel
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  
  // Modal détail catégorie
  const [showCategoryDetail, setShowCategoryDetail] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Utiliser seulement le dashboard qui contient les vraies données classifiées
        const dashboard = await api.getDashboard(year, month);
        setDashboardData(dashboard);
        setTransactions([]); // Plus besoin de l'ancienne collection
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, viewType]);

  // Utiliser les données du dashboard au lieu de transactions
  const depensesData = dashboardData?.kpis?.[selectedDept] || dashboardData?.kpis?.GLOBAL || {};
  const expenseCategories = depensesData.expense_categories || [];
  
  // Filtrer les dépenses par département
  const filteredCategories = selectedDept === "GLOBAL"
    ? (dashboardData?.kpis?.GLOBAL?.expense_categories || [])
    : expenseCategories;

  // Grouper par catégorie (déjà fait par le backend!)
  const depensesByCategory = {};
  filteredCategories.forEach(cat => {
    depensesByCategory[cat.categorie] = {
      total: cat.montant,
      count: 1
    };
  });

  // Préparer données pour graphiques
  const pieData = Object.entries(depensesByCategory)
    .map(([cat, data]) => ({
      name: cat,  // Utiliser le nom complet de la catégorie
      value: data.total,
      category: cat
    }))
    .sort((a, b) => b.value - a.value);

  const totalDepenses = Object.values(depensesByCategory).reduce((sum, cat) => sum + cat.total, 0);

  // Dépenses par type (variable vs fixe) - Simplification: tout est variable pour l'instant
  const depensesByType = {
    variable: totalDepenses,
    fixe: 0
  };

  // Calculs pour KPIs
  const fraisVariables = totalDepenses;
  const fraisFixes = 0;
  const revenus = dashboardData?.kpis?.[selectedDept]?.revenus?.total || dashboardData?.kpis?.GLOBAL?.revenus?.total || 0;
  const pctFraisFixes = revenus > 0 ? (fraisFixes / revenus) * 100 : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  return (
    <div data-testid="depenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Analyse des Dépenses</h1>
          <p className="text-slate-500 mt-1">{viewType === "mensuel" ? MONTHS[month] : viewType === "trimestriel" ? `T${Math.ceil(month/3)}` : "Année"} {year}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-40 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">Tous départements</SelectItem>
              {CONFIG.departments.map(d => (
                <SelectItem key={d} value={d}>{CONFIG.department_names[d]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={viewType} onValueChange={setViewType}>
            <SelectTrigger className="w-32 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mensuel">Mensuel</SelectItem>
              <SelectItem value="trimestriel">Trimestriel</SelectItem>
              <SelectItem value="annuel">Annuel</SelectItem>
            </SelectContent>
          </Select>
          {viewType === "mensuel" && (
            <PeriodSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
          )}
          {viewType !== "mensuel" && (
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Dépenses totales"
          value={formatCurrency(totalDepenses)}
          subtitle={`${filteredCategories.length} catégories`}
          icon={Receipt}
          color="red"
          description="Total de toutes les dépenses pour la période et le département sélectionnés."
          kpiKey="depenses_total"
          onClick={() => {
            setSelectedKPI({title: "Dépenses Totales", kpiKey: "depenses_total", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Frais variables"
          value={formatCurrency(fraisVariables)}
          subtitle={`${formatPercent((fraisVariables / totalDepenses) * 100 || 0)} des dépenses`}
          icon={TrendingDown}
          color="amber"
          description="Frais qui varient selon le volume d'activité: salaires terrain, matériaux, essence, sous-traitants, publicité."
          kpiKey="frais_variables"
          onClick={() => {
            setSelectedKPI({title: "Frais Variables", kpiKey: "frais_variables", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Frais fixes"
          value={formatCurrency(fraisFixes)}
          subtitle={`${formatPercent(pctFraisFixes)} du CA`}
          icon={Home}
          color="blue"
          description="Frais qui restent constants: loyer, assurances, télécom, SaaS, salaires admin, dépréciation, intérêts."
          kpiKey="frais_fixes"
          onClick={() => {
            setSelectedKPI({title: "Frais Fixes", kpiKey: "frais_fixes", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Coût moyen / catégorie"
          value={formatCurrency(totalDepenses / (filteredCategories.length || 1))}
          subtitle="Par catégorie"
          icon={Calculator}
          color="purple"
          kpiKey="cout_moyen"
          onClick={() => {
            setSelectedKPI({title: "Coût Moyen", kpiKey: "cout_moyen", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Répartition par catégorie */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend 
                    layout="vertical" 
                    align="right" 
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: '11px', maxHeight: '300px', overflowY: 'auto' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top catégories */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Top catégories de dépenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pieData.slice(0, 8)} layout="vertical">
                  <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill={CHART_COLORS.depenses} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste détaillée par catégorie */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Détail par catégorie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pieData.map((cat, idx) => {
              const CategoryIcon = CATEGORY_ICONS[cat.category] || Receipt;
              const catInfo = EXPENSE_CATEGORIES[cat.category];
              return (
                <div key={cat.category} className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${CATEGORY_COLORS[idx]}20` }}>
                        <CategoryIcon className="w-4 h-4" style={{ color: CATEGORY_COLORS[idx] }} />
                      </div>
                      <span className="font-medium text-slate-900">{cat.name}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {catInfo?.type === "fixe" ? "Fixe" : "Variable"}
                    </Badge>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{formatCurrency(cat.value)}</p>
                      <p className="text-xs text-slate-500">
                        {formatPercent((cat.value / totalDepenses) * 100)} du total
                      </p>
                    </div>
                    {revenus > 0 && (
                      <p className="text-sm text-slate-500">
                        {formatPercent((cat.value / revenus) * 100)} du CA
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Transactions récentes */}
      <Card className="animate-fade-in delay-400">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Dernières transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase">Description</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase">Catégorie</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase">Département</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-slate-500 uppercase">Montant</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.slice(0, 15).map((cat, idx) => (
                  <tr 
                    key={cat.id || cat._id || idx} 
                    className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedCategory(cat.categorie);
                      setShowCategoryDetail(true);
                    }}
                  >
                    <td className="py-3 px-4 text-sm text-slate-600">Multiples</td>
                    <td className="py-3 px-4 text-sm text-slate-900 font-medium">{cat.categorie}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-xs">
                        {cat.categorie}
                      </Badge>
                    </td>
                    <td className="py-3 px-4"><DepartmentBadge dept={selectedDept} /></td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-red-600">{formatCurrency(cat.montant)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}
      
      {/* Modal Détail Catégorie */}
      {showCategoryDetail && selectedCategory && (
        <CategoryDetailModal
          open={showCategoryDetail}
          onClose={() => {
            setShowCategoryDetail(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          year={year}
          month={month}
          departement={selectedDept}
        />
      )}
    </div>
  );
};

export default Depenses;
